// api.js
import { format } from 'date-fns';
import axiosInstance from './axios';
import authService from './auth';
import roleService from './role';
import syncService from './sync';
import reportService from './report';

const API = {
  // Employee endpoints
  getEmployees: async () => {
    try {
      const response = await axiosInstance.get('/employees');
      if (response.status !== 200) {
        throw new Error('Failed to fetch employees');
      }
      return response.data;
    } catch (error) {
      console.error('Error fetching employees:', error);
      throw error;
    }
  },

  // Project endpoints
  getProjects: async (employeeId, orderBy, orderDirection) => {
    const params = new URLSearchParams();
    if (orderBy) params.set('orderBy', orderBy);
    if (orderDirection) params.set('orderDirection', orderDirection);
    const paramsString = params.toString();
    try {
      const response = await axiosInstance.get(
        `/projects/employee/${employeeId}${
          paramsString ? `?${paramsString}` : ''
        }`,
      );
      if (response.status !== 200) {
        throw new Error('Failed to fetch projects');
      }
      return response.data;
    } catch (error) {
      console.error('Error fetching projects:', error);
      throw error;
    }
  },

  // Hours endpoints
  getMonthlyHours: async (employeeId, year, month) => {
    try {
      const response = await axiosInstance.get(
        `/hours/monthly/${employeeId}?year=${year}&month=${month}`,
      );
      if (response.status !== 200) {
        throw new Error('Failed to fetch monthly hours');
      }
      return response.data;
    } catch (error) {
      console.error('Error fetching monthly hours:', error);
      throw error;
    }
  },

  getTotalProjectHours: async (projectId) => {
    try {
      const response = await axiosInstance.get(`/hours/total/${projectId}`);
      if (response.status !== 200) {
        throw new Error('Failed to fetch total project hours');
      }
      return response.data;
    } catch (error) {
      console.error('Error fetching total project hours:', error);
      throw error;
    }
  },

  getTeamHours: async (projectId, startDate, endDate) => {
    try {
      const formattedStartDate = format(startDate, 'yyyy-MM-dd');
      const formattedEndDate = format(endDate, 'yyyy-MM-dd');

      const response = await axiosInstance.get(
        `/hours/team/${projectId}?start=${formattedStartDate}&end=${formattedEndDate}`,
      );
      if (response.status !== 200) {
        throw new Error('Failed to fetch team hours');
      }
      return response.data;
    } catch (error) {
      console.error('Error fetching team hours:', error);
      throw error;
    }
  },

  saveHours: async (data) => {
    try {
      console.log('Sending save request with data:', data);

      const response = await axiosInstance.post('/hours', {
        employeeId: parseInt(data.employeeId),
        projectId: parseInt(data.projectId),
        hours: parseFloat(data.hours),
        startDate: data.startDate,
        endDate: data.endDate,
      });

      if (response.status !== 200) {
        const errorData = response.data;
        console.error('Server error response:', errorData);
        throw new Error(errorData.error || `Server error: ${response.status}`);
      }

      const result = response.data;
      console.log('Save successful:', result);
      return result;
    } catch (error) {
      console.error('API error:', error);
      throw error;
    }
  },

  // Assignment endpoints
  getAssignmentDetails: async (projectId, employeeId) => {
    try {
      const response = await axiosInstance.get(
        `/assignments/${projectId}/${employeeId}`,
      );
      if (response.status !== 200) {
        throw new Error('Failed to fetch assignment details');
      }
      return response.data;
    } catch (error) {
      console.error('Error fetching assignment details:', error);
      throw error;
    }
  },

  // Project details endpoint
  getProjectDetails: async (projectId) => {
    try {
      const response = await axiosInstance.get(
        `/projects/details/${projectId}`,
      );
      if (response.status !== 200) {
        throw new Error('Failed to fetch project details');
      }
      return response.data;
    } catch (error) {
      console.error('Error fetching project details:', error);
      throw error;
    }
  },

  // Project summary endpoint
  getProjectSummary: async (projectId) => {
    try {
      const response = await axiosInstance.get(
        `/projects/${projectId}/summary`,
      );
      if (response.status !== 200) {
        throw new Error('Failed to fetch project summary');
      }
      return response.data;
    } catch (error) {
      console.error('Error fetching project summary:', error);
      throw error;
    }
  },

  // Project estimate endpoint
  updateProjectEstimate: async (projectId, data) => {
    try {
      const response = await axiosInstance.put(
        `/projects/${projectId}/estimate`,
        {
          data,
        },
      );
      if (!response.ok) {
        throw new Error('Failed to update project estimate');
      }
      return response.data;
    } catch (error) {
      console.error('Error updating project estimate:', error);
      throw error;
    }
  },

  auth: authService,
  role: roleService,
  sync: syncService,
  report: reportService,
};

export default API;
